import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { LIST_BY_RUBRICS } from 'src/constants/routes';
import { IRubricResultModel } from 'src/interfaces/news';
import { createLinkProps } from 'src/utils/routing';

import { Typography } from '@sravni/react-design-system';
import { useBoolean } from '@sravni/react-utils';

import styles from './item.module.scss';

interface Props {
  item: IRubricResultModel;
}

export const Item = ({ item }: Props) => {
  const itemRef = useRef(null);
  const [isVisibleItem, setIsVisibleItem] = useBoolean(true);
  const {
    query: { rubric, rubricAlias },
  } = useRouter();

  const { href, as } = createLinkProps('list', LIST_BY_RUBRICS, {
    rubric: item.alias,
  });

  const isActive = item.alias === rubric || item.alias === rubricAlias;

  useEffect(() => {
    const currentRef = itemRef.current;

    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px 0px -94px 0px',
      threshold: 0,
    };

    if (currentRef) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const { y } = entry.boundingClientRect;
          const windowHeight = window.innerHeight || document.documentElement.clientHeight;

          if (entry.isIntersecting) {
            setIsVisibleItem.off();
          } else if (y > windowHeight) {
            setIsVisibleItem.on();
          } else if (!isVisibleItem) {
            setIsVisibleItem.on();
          }
        });
      }, options);

      observer.observe(currentRef);
      return () => {
        observer.unobserve(currentRef);
      };
    }
  }, [isVisibleItem, setIsVisibleItem]);

  return (
    <Typography.Text
      ref={itemRef}
      size={14}
      strong
      className={classNames(styles.item, 'h-mt-24', isVisibleItem ? styles['item--hide'] : styles['item--visible'])}
      key={`rubric-${item.alias}`}
    >
      <Link href={href} as={as}>
        <Typography.Link
          href={as}
          color={'black'}
          className={classNames(styles.itemLink, { [styles[`itemLink--active`]]: isActive })}
        >
          {item.iconUrl && (
            <img width={36} height={36} src={item.iconUrl} className={styles.itemIcon} alt={'Иконка рубрики'} />
          )}
          {item.name}
        </Typography.Link>
      </Link>
    </Typography.Text>
  );
};
